import { useState, useEffect, useRef, useMemo } from "react";
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { FiActivity, FiAward, FiCalendar, FiClock, FiFlag, FiPlay, FiTrendingUp, FiDownload, FiShare2, FiZap, FiCheckCircle, FiX, FiChevronLeft, FiChevronRight, FiLock } from "react-icons/fi";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { useCheckIn } from "../../context/CheckInContext";
import { userAPI, liveSessionAPI, achievementAPI } from "../../services/api";
import { isFeaturePermitted } from "../../utils/featureAccess";
import { toast } from "react-toastify";

const getLocalDateString = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// ─── 3D TILT CARD ────────────────────────────────────────────────────────────
function TiltCard({ children, className = "", style = {}, intensity = 8 }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [intensity, -intensity]), { stiffness: 250, damping: 28 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-intensity, intensity]), { stiffness: 250, damping: 28 });

  return (
    <motion.div
      ref={ref}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        x.set((e.clientX - r.left) / r.width - 0.5);
        y.set((e.clientY - r.top) / r.height - 0.5);
      }}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 900, ...style }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── ANIMATED COUNT-UP ────────────────────────────────────────────────────────
function AnimCount({ to, duration = 1200 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const pct = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(pct * to));
      if (pct < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [to, duration]);
  return <span>{val}</span>;
}

// ─── FLOATING PARTICLES ───────────────────────────────────────────────────────
function Particles() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {Array.from({ length: 16 }, (_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: Math.random() * 5 + 2,
            height: Math.random() * 5 + 2,
            background: i % 3 === 0 ? "rgba(242,154,12,0.4)" : i % 3 === 1 ? "rgba(107,138,90,0.3)" : "rgba(233,120,24,0.2)",
          }}
          animate={{ y: [-20, 20, -20], x: [-10, 10, -10], opacity: [0.2, 0.8, 0.2] }}
          transition={{ duration: Math.random() * 8 + 6, repeat: Infinity, delay: Math.random() * 4, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

// ─── GLASS STYLE ─────────────────────────────────────────────────────────────
const glass = {
  background: "rgba(255,255,255,0.8)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── FILTER PILL ─────────────────────────────────────────────────────────────
function FilterPill({ label, active, onClick }) {
  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.95 }}
      className="px-4 py-1.5 rounded-full text-[12px] font-bold cursor-pointer border-0 outline-none"
      style={active
        ? { background: "linear-gradient(135deg,#F29A0C,#E97818)", color: "#fff", boxShadow: "0 6px 18px rgba(242,154,12,0.35)", fontFamily: "'Inter',sans-serif" }
        : { background: "rgba(255,255,255,0.8)", color: "#888", border: "1px solid rgba(239,229,216,0.7)", fontFamily: "'Inter',sans-serif" }
      }
    >
      {label}
    </motion.button>
  );
}

// ─── HERO HEADER ─────────────────────────────────────────────────────────────
function HeroHeader({ profile, stats, onExport, onShare }) {
  const startMonthStr = useMemo(() => {
    if (!profile?.createdAt) return "January 2026";
    return new Date(profile.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }, [profile]);

  const monthsOfGrowth = useMemo(() => {
    if (!profile?.createdAt) return "1 month";
    const diff = new Date() - new Date(profile.createdAt);
    const months = Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24 * 30.4)));
    return `${months} ${months === 1 ? 'month' : 'months'}`;
  }, [profile]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
      className="relative overflow-hidden rounded-[28px] sm:rounded-[36px] p-5 sm:p-8 md:p-10 mb-5"
      style={glass}
    >
      {/* Dot grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
        style={{ backgroundImage: "radial-gradient(circle,#000 1px,transparent 0)", backgroundSize: "22px 22px" }} />
      <div className="absolute top-0 right-0 w-56 h-56 pointer-events-none"
        style={{ background: "radial-gradient(circle,rgba(242,154,12,0.1),transparent)", filter: "blur(40px)" }} />

      <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-8">
        <div className="w-full flex-1 text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-3 mb-4 sm:mb-5">
            <div className="w-11 h-11 rounded-[16px] flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", boxShadow: "0 8px 22px rgba(242,154,12,0.4)" }}>
              <FiActivity style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div className="text-left">
              <p className="text-[11px] font-bold text-[#F29A0C] uppercase tracking-[1.5px]">My Journey</p>
              <p className="text-[12px] text-[#AAA]">Since {startMonthStr} · {monthsOfGrowth} of growth</p>
            </div>
          </div>

          <h1 className="font-black text-[#1F1F1F] mb-3 text-[26px] sm:text-[36px] lg:text-[44px]"
            style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-1.2px", lineHeight: 1.1 }}>
            Every step has{" "}
            <span style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              shaped you.
            </span>
          </h1>

          <p className="text-[13px] sm:text-[15px] text-[#999] leading-[1.7] max-w-[650px] mb-6 sm:mb-7 mx-auto md:mx-0">
            Your complete wellness story · every session attended, every milestone unlocked, every streak earned. This is your legacy on the mat.
          </p>

          <div className="flex flex-wrap sm:flex-nowrap justify-center md:justify-start gap-3">
            <motion.button whileHover={{ y: -2, scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={onExport}
              className="h-[44px] px-5 sm:px-6 rounded-full text-white text-[13px] font-bold flex items-center justify-center gap-2 cursor-pointer flex-1 sm:flex-initial"
              style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", boxShadow: "0 10px 28px rgba(242,154,12,0.38)", fontFamily: "'Inter',sans-serif" }}>
              <FiDownload size={14} /> Export Journey
            </motion.button>
            <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
              onClick={onShare}
              className="h-[44px] px-5 sm:px-6 rounded-full text-[#666] text-[13px] font-semibold flex items-center justify-center gap-2 cursor-pointer flex-1 sm:flex-initial"
              style={{ background: "rgba(255,255,255,0.9)", border: "1px solid rgba(239,229,216,0.9)", fontFamily: "'Inter',sans-serif" }}>
              <FiShare2 size={13} /> Share Progress
            </motion.button>
          </div>
        </div>

        {/* Dual ring score (Row 2 on mobile, Column 2 on desktop) */}
        <div className="flex-shrink-0 text-center mx-auto md:mx-0 pt-2 md:pt-0">
          <div className="relative w-44 h-44 sm:w-48 sm:h-48 mx-auto">
            <svg className="absolute inset-0 w-full h-full" style={{ transform: "rotate(-90deg)" }} viewBox="0 0 192 192">
              <circle cx="96" cy="96" r="78" stroke="#F1E8DA" strokeWidth="12" fill="none" />
              <motion.circle cx="96" cy="96" r="78" stroke="url(#hg1)" strokeWidth="12" fill="none" strokeLinecap="round"
                strokeDasharray="490" initial={{ strokeDashoffset: 490 }} animate={{ strokeDashoffset: 490 - (490 * (stats?.wellnessScore ?? 0)) / 100 }}
                transition={{ duration: 2, delay: 0.4, ease: "easeOut" }} />
              <circle cx="96" cy="96" r="56" stroke="#ECF5E8" strokeWidth="8" fill="none" />
              <motion.circle cx="96" cy="96" r="56" stroke="url(#hg2)" strokeWidth="8" fill="none" strokeLinecap="round"
                strokeDasharray="351" initial={{ strokeDashoffset: 351 }} animate={{ strokeDashoffset: 351 - (351 * Math.min(100, (stats?.streakDays || 0) * 10)) / 100 }}
                transition={{ duration: 2, delay: 0.6, ease: "easeOut" }} />
              <defs>
                <linearGradient id="hg1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#F29A0C" /><stop offset="100%" stopColor="#E97818" />
                </linearGradient>
                <linearGradient id="hg2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#6B8A5A" /><stop offset="100%" stopColor="#8BA47A" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="font-black text-[#1F1F1F] leading-none text-[38px] sm:text-[44px]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-1.5px" }}>
                <AnimCount to={stats?.wellnessScore ?? 0} />
              </p>
              <p className="text-[11px] text-[#AAA] font-medium mt-0.5">Journey Score</p>
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-3">
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-[3px]" style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }} /><span className="text-[11px] text-[#AAA]">Overall</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-[3px]" style={{ background: "linear-gradient(135deg,#6B8A5A,#8BA47A)" }} /><span className="text-[11px] text-[#AAA]">Consistency</span></div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─── STATS BAND ───────────────────────────────────────────────────────────────
function StatsBand({ stats }) {
  const bestStreak = stats.bestStreak || stats.streakDays;
  const badgesEarned = stats.badgesEarned ?? 0;
  const isAchievementsLocked = stats.isAchievementsLocked;
  const isAttendanceLocked = stats.isAttendanceLocked;

  const badgeSub = isAchievementsLocked 
    ? "Upgrade Plan to Unlock" 
    : stats.totalBadges 
      ? `${badgesEarned} of ${stats.totalBadges} unlocked` 
      : "From milestones";

  const statsData = [
    { icon: FiZap, val: stats.streakDays, label: "Current Streak", sub: bestStreak > 0 ? `Best Record: ${bestStreak}d` : "Keep it going!", accent: "#F29A0C", bg: "linear-gradient(145deg,#FFF4E8,#FFF8EE)", isLocked: false },
    { icon: isAttendanceLocked ? FiLock : FiCalendar, val: stats.totalPractices, label: "Sessions Total", sub: isAttendanceLocked ? "Upgrade Plan to Unlock" : `${stats.totalPractices} completed`, accent: isAttendanceLocked ? "#888888" : "#6B8A5A", bg: isAttendanceLocked ? "linear-gradient(145deg,#F3F4F6,#F9FAFB)" : "linear-gradient(145deg,#F5FAF2,#F9FDF5)", isLocked: isAttendanceLocked },
    { icon: isAttendanceLocked ? FiLock : FiClock, val: Math.round(stats.totalPractices * 0.75), label: "Hours Practiced", sub: isAttendanceLocked ? "Upgrade Plan to Unlock" : "Based on 45m classes", accent: isAttendanceLocked ? "#888888" : "#F29A0C", bg: isAttendanceLocked ? "linear-gradient(145deg,#F3F4F6,#F9FAFB)" : "linear-gradient(145deg,#FFF4E8,#FFF8EE)", isLocked: isAttendanceLocked },
    { icon: isAchievementsLocked ? FiLock : FiAward, val: badgesEarned, label: "Badges Earned", sub: badgeSub, accent: isAchievementsLocked ? "#888888" : "#6B8A5A", bg: isAchievementsLocked ? "linear-gradient(145deg,#F3F4F6,#F9FAFB)" : "linear-gradient(145deg,#F5FAF2,#F9FDF5)", isLocked: isAchievementsLocked },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
      className="ud-kpi grid grid-cols-2 sm:grid-cols-4 mb-5 rounded-[24px] sm:rounded-[28px] overflow-hidden"
      style={{ border: "1px solid rgba(255,255,255,0.9)", boxShadow: "0 16px 50px rgba(0,0,0,0.06)" }}
    >
      {statsData.map((s, i) => (
        <motion.div key={s.label}
          whileHover={{ background: "rgba(255,255,255,0.95)" }}
          className="relative overflow-hidden p-4 sm:p-7"
          style={{ background: s.bg, borderRight: (i % 2 === 0 || i < 3) ? "1px solid rgba(239,229,216,0.5)" : "none", borderBottom: i < 2 ? "1px solid rgba(239,229,216,0.5)" : "none" }}
        >
          <div className="absolute top-0 right-0 w-24 h-24 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle,${s.accent}20,transparent)`, filter: "blur(20px)", transform: "translate(30%,-30%)" }} />
          <div className="absolute bottom-0 left-0 right-0 h-[3px]"
            style={{ background: `linear-gradient(90deg,${s.accent},${s.accent}88)` }} />
          <div className="ud-kpi-icon w-10 h-10 rounded-[14px] flex items-center justify-center mb-4"
            style={{ background: `linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow: `0 8px 20px ${s.accent}40` }}>
            <s.icon style={{ color: "#fff", fontSize: 18 }} />
          </div>
          <div>
            {s.isLocked ? (
              <p className="ud-kpi-val font-black text-zinc-400 leading-none mb-1"
                style={{ fontFamily: "'Poppins',sans-serif", fontSize: 32, letterSpacing: "-1px" }}>
                Locked
              </p>
            ) : (
              <p className="ud-kpi-val font-black text-[#1F1F1F] leading-none mb-1"
                style={{ fontFamily: "'Poppins',sans-serif", fontSize: 42, letterSpacing: "-1.5px" }}>
                <AnimCount to={s.val} duration={1000 + i * 100} />
              </p>
            )}
            <p className="ud-kpi-lbl text-[10px] font-bold text-[#AAA] uppercase tracking-[0.8px] mb-2.5">{s.label}</p>
            <p className="ud-kpi-sub text-[11px] font-bold flex items-center gap-1" style={{ color: s.accent }}>
              {s.isLocked ? <FiLock size={11} /> : <FiTrendingUp size={11} />} {s.sub}
            </p>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

// ─── WELLNESS SCORE TREND ─────────────────────────────────────────────────────
// ─── WELLNESS SCORE TREND CHART ───────────────────────────────────────────────
function TrendChart({ data }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  
  if (!data || !data.items || data.items.length === 0) {
    return (
      <div className="h-[200px] flex flex-col items-center justify-center text-center p-6 rounded-2xl border"
        style={{ background: "rgba(255, 248, 238, 0.6)", borderColor: "rgba(242,154,12,0.18)" }}>
        <FiActivity className="text-[#F29A0C] mb-2" size={32} />
        <p className="text-[14px] font-bold text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>Starting Your Wellness Journey</p>
        <p className="text-[12px] text-[#999] max-w-sm mt-1">Check in today to unlock your active weekly wellness momentum and score tracking!</p>
      </div>
    );
  }

  const items = data.items;
  const n = items.length;
  const W = 620, H = 145;
  const paddingLeft = 44;
  const paddingRight = 24;
  const chartW = W - paddingLeft - paddingRight;
  
  const minS = 0;
  const maxS = 100;
  
  const pts = items.map((item, i) => {
    const x = paddingLeft + (n === 1 ? chartW / 2 : (i / (n - 1)) * chartW);
    const scoreVal = Math.min(100, Math.max(0, item.score || 0));
    const y = H - ((scoreVal - minS) / (maxS - minS || 1)) * H;
    return { x, y, item, score: scoreVal };
  });

  const isSingle = n === 1;
  const linePath = isSingle
    ? `M${paddingLeft},${pts[0].y} L${W - paddingRight},${pts[0].y}`
    : pts.map((p, i) => i === 0 ? `M${p.x},${p.y}` : `C${pts[i-1].x + (p.x - pts[i-1].x)*0.45},${pts[i-1].y} ${p.x - (p.x - pts[i-1].x)*0.45},${p.y} ${p.x},${p.y}`).join(" ");

  const areaPath = isSingle
    ? `M${paddingLeft},${pts[0].y} L${W - paddingRight},${pts[0].y} L${W - paddingRight},${H} L${paddingLeft},${H} Z`
    : linePath + ` L${pts[n-1].x},${H} L${pts[0].x},${H} Z`;

  const activeIdx = hoveredIdx !== null ? hoveredIdx : (n - 1);
  const activePt = pts[activeIdx] || pts[n - 1];

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${W} ${H + 34}`} style={{ width: "100%", height: 195, overflow: "visible" }}>
        <defs>
          <linearGradient id="tGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F29A0C" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#F29A0C" stopOpacity="0.0" />
          </linearGradient>
          <linearGradient id="lGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#F29A0C" /><stop offset="100%" stopColor="#E97818" />
          </linearGradient>
        </defs>
        
        {/* Y-Axis lines and numbers */}
        {[0, 25, 50, 75, 100].map(val => {
          const y = H - (val / 100) * H;
          return (
            <g key={val}>
              <text x="6" y={y + 3.5} fontSize="9.5" fill="#BBB" fontWeight="600" fontFamily="Inter">{val}</text>
              <line x1={paddingLeft} y1={y} x2={W - paddingRight} y2={y} stroke="rgba(239,229,216,0.38)" strokeWidth="1" strokeDasharray={val === 0 ? "none" : "3 3"} />
            </g>
          );
        })}
        
        {/* Area fill */}
        <motion.path d={areaPath} fill="url(#tGrad)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }} />
        
        {/* Smooth line */}
        <motion.path d={linePath} fill="none" stroke="url(#lGrad)" strokeWidth={isSingle ? "2" : "3.5"}
          strokeDasharray={isSingle ? "4 4" : "none"} strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, delay: 0.1, ease: "easeOut" }} />
        
        {/* Points & Interactive Nodes */}
        {pts.map((p, i) => {
          const isSelected = activeIdx === i;
          return (
            <g key={i} className="cursor-pointer"
               onMouseEnter={() => setHoveredIdx(i)}
               onMouseLeave={() => setHoveredIdx(null)}
               onClick={() => setHoveredIdx(i)}>
              
              {/* Highlight drop guide line */}
              {isSelected && (
                <line x1={p.x} y1={p.y} x2={p.x} y2={H} stroke="#F29A0C" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.6" />
              )}

              {/* Pulsing Aura for current week */}
              {p.item.isCurrent && (
                <motion.circle cx={p.x} cy={p.y} r={11} fill="#F29A0C" opacity="0.2"
                  animate={{ scale: [1, 1.35, 1], opacity: [0.25, 0.05, 0.25] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
              )}

              {/* Circle Node */}
              <motion.circle cx={p.x} cy={p.y} r={isSelected ? 6.5 : (p.item.isCurrent ? 5.5 : 4)}
                fill={isSelected ? "#F29A0C" : (p.item.isCurrent ? "#E97818" : "#fff")}
                stroke={isSelected ? "#fff" : "#F29A0C"} strokeWidth={isSelected ? "2.5" : "2"}
                initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 + i * 0.06, type: "spring" }} />
              
              {/* Tooltip badge on active point */}
              {isSelected && (
                <g>
                  <rect x={p.x - 18} y={p.y - 28} width="36" height="19" rx="5" fill="#1F1F1F" filter="drop-shadow(0 2px 6px rgba(0,0,0,0.25))" />
                  <text x={p.x} y={p.y - 15} textAnchor="middle" fontSize="10.5" fill="#fff" fontWeight="800" fontFamily="Inter">
                    {p.score}
                  </text>
                  <polygon points={`${p.x-4},${p.y-9} ${p.x+4},${p.y-9} ${p.x},${p.y-5}`} fill="#1F1F1F" />
                </g>
              )}
              
              {/* Invisible touch/hover target */}
              <circle cx={p.x} cy={p.y} r="20" fill="transparent" />
            </g>
          );
        })}
        
        {/* X-Axis labels */}
        {pts.map((p, i) => {
          const isSelected = activeIdx === i;
          return (
            <g key={i} className="cursor-pointer" onClick={() => setHoveredIdx(i)}>
              <text x={p.x} y={H + 18} textAnchor="middle" fontSize="11"
                fill={p.item.isCurrent ? "#E97818" : (isSelected ? "#1F1F1F" : "#888")}
                fontWeight={p.item.isCurrent || isSelected ? "800" : "600"} fontFamily="Poppins, Inter">
                {p.item.label}
              </text>
              {p.item.isCurrent && (
                <text x={p.x} y={H + 30} textAnchor="middle" fontSize="8.5" fill="#E97818" fontWeight="700">
                  (Now)
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Dynamic Glass Details Strip */}
      <AnimatePresence>
        {activePt && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.15 }}
            className="mt-2.5 rounded-[18px] p-3 flex flex-wrap items-center justify-between gap-3 border shadow-sm"
            style={{ background: "rgba(255, 248, 238, 0.88)", borderColor: "rgba(242,154,12,0.2)" }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-black text-[12px]"
                style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }}>
                {activePt.item.isCurrent ? "✦" : `W${activePt.item.weekNum}`}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-[12.5px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                    {activePt.item.isCurrent ? `Week ${activePt.item.weekNum} · Active Now` : `Week ${activePt.item.weekNum} Wellness Score`}
                  </p>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${activePt.item.isCurrent ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    {activePt.item.isCurrent ? "In Progress" : "Completed"}
                  </span>
                </div>
                <p className="text-[11px] text-zinc-500 font-medium">{activePt.item.dateRangeStr} • {activePt.item.attendedCount} session{activePt.item.attendedCount === 1 ? '' : 's'} logged</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-400">Score Rating</p>
                <p className="text-[17px] font-black text-[#E97818] leading-none" style={{ fontFamily: "'Poppins',sans-serif" }}>
                  {activePt.score} <span className="text-[11px] text-zinc-400 font-normal">/ 100</span>
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── TIMELINE CARD ────────────────────────────────────────────────────────────
function TimelineCard({ item, delay }) {
  const nodeColors = {
    orange: "linear-gradient(135deg,#F29A0C,#E97818)",
    olive: "linear-gradient(135deg,#6B8A5A,#8BA47A)",
  };
  const bgColors = {
    orange: { bg: "rgba(255,244,232,0.8)", border: "rgba(242,154,12,0.15)", hover: "rgba(255,244,232,1)" },
    olive: { bg: "rgba(245,250,242,0.8)", border: "rgba(107,138,90,0.15)", hover: "rgba(245,250,242,1)" },
    neutral: { bg: "rgba(255,255,255,0.7)", border: "rgba(239,229,216,0.7)", hover: "rgba(255,255,255,0.95)" },
  };
  const c = bgColors[item.color] || bgColors.neutral;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="mb-7 relative"
    >
      {/* Node */}
      <div className="absolute -left-[32px] top-[10px] w-[18px] h-[18px] rounded-full flex items-center justify-center z-10"
        style={{ background: nodeColors[item.color] || nodeColors.orange, boxShadow: `0 4px 12px ${item.color === "olive" ? "rgba(107,138,90,0.5)" : "rgba(242,154,12,0.5)"}` }}>
        {item.isActive && <span className="absolute w-full h-full rounded-full animate-ping" style={{ background: "rgba(242,154,12,0.3)" }} />}
        <item.icon style={{ fontSize: 8, color: "#fff" }} />
      </div>

      <motion.div
        whileHover={{ x: 6 }}
        className="rounded-[20px] p-4 cursor-default"
        style={{ background: c.bg, border: `1px solid ${c.border}`, transition: "background 0.25s" }}
        onMouseEnter={e => e.currentTarget.style.background = c.hover}
        onMouseLeave={e => e.currentTarget.style.background = c.bg}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-[14px] font-bold text-[#1F1F1F]">{item.title}</p>
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full"
            style={item.color === "orange" ? { background: "rgba(242,154,12,0.1)", color: "#C8660A" } : item.color === "olive" ? { background: "rgba(107,138,90,0.1)", color: "#3E5C2E" } : { background: "rgba(200,200,200,0.2)", color: "#888" }}>
            {item.time}
          </span>
        </div>
        <p className="text-[12px] text-[#888] leading-[1.65]">{item.desc}</p>
        <div className="flex gap-2 mt-3 flex-wrap">
          {item.tags.map(t => (
            <span key={t} className="text-[10px] font-bold px-2.5 py-1 rounded-full"
              style={item.color === "orange" ? { background: "rgba(242,154,12,0.1)", color: "#C8660A" } : item.color === "olive" ? { background: "rgba(107,138,90,0.1)", color: "#3E5C2E" } : { background: "rgba(200,200,200,0.15)", color: "#888" }}>
              {t}
            </span>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── DATA BINDING HELPERS ──────────────────────────────────────────────────
const getPersonalBestStreak = (attendanceHistory) => {
  if (!attendanceHistory || attendanceHistory.length === 0) return 0;
  const sortedDates = Array.from(new Set(
    attendanceHistory.map(a => getLocalDateString(a.attendanceDate))
  )).sort((a, b) => new Date(a) - new Date(b));

  let maxStreak = 0;
  let currentStreak = 0;
  let prevDate = null;

  for (let i = 0; i < sortedDates.length; i++) {
    const currentDate = new Date(sortedDates[i]);
    if (!prevDate) {
      currentStreak = 1;
    } else {
      const diffTime = Math.abs(currentDate - prevDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays === 1) {
        currentStreak++;
      } else if (diffDays > 1) {
        if (currentStreak > maxStreak) {
          maxStreak = currentStreak;
        }
        currentStreak = 1;
      }
    }
    prevDate = currentDate;
  }
  if (currentStreak > maxStreak) {
    maxStreak = currentStreak;
  }
  return maxStreak;
};

const getTrendData = (profile, attendanceHistory, stats) => {
  const now = new Date();
  now.setHours(23, 59, 59, 999);

  // 1. Determine user registration / initiation date
  let regDate = profile?.createdAt ? new Date(profile.createdAt) : null;
  if (!regDate || isNaN(regDate.getTime())) {
    if (attendanceHistory && attendanceHistory.length > 0) {
      const sorted = [...attendanceHistory].sort((a, b) => new Date(a.attendanceDate) - new Date(b.attendanceDate));
      regDate = new Date(sorted[0].attendanceDate);
    } else {
      regDate = new Date(now);
    }
  }
  regDate.setHours(0, 0, 0, 0);

  // 2. Compute total elapsed days & weeks since registration
  const diffTime = Math.max(0, now.getTime() - regDate.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.max(1, Math.floor(diffDays / 7) + 1);

  const allWeeks = [];

  for (let k = 1; k <= totalWeeks; k++) {
    const isCurrent = k === totalWeeks;
    const weekStart = new Date(regDate.getTime() + (k - 1) * 7 * 86400000);
    weekStart.setHours(0, 0, 0, 0);

    const weekEnd = isCurrent 
      ? new Date(now) 
      : new Date(regDate.getTime() + (k * 7 - 1) * 86400000);
    weekEnd.setHours(23, 59, 59, 999);

    const startLabel = weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const endLabel = weekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const dateRangeStr = isCurrent && diffDays === 0 ? "Started Today" : `${startLabel} – ${endLabel}`;

    // Filter check-ins during this week
    const weekAtts = (attendanceHistory || []).filter(a => {
      if (!a.attendanceDate) return false;
      const d = new Date(a.attendanceDate);
      return d >= weekStart && d <= weekEnd;
    });

    const uniqueDates = new Set(weekAtts.map(a => getLocalDateString(a.attendanceDate)));
    const attendedDaysCount = uniqueDates.size;

    let weekScore = 0;
    if (isCurrent) {
      weekScore = stats?.wellnessScore ?? 0;
      if (weekScore === 0 && attendedDaysCount > 0) {
        weekScore = Math.min(100, Math.round((attendedDaysCount / 7) * 70 + attendedDaysCount * 5));
      }
    } else {
      const consistencyPct = attendedDaysCount / 7;
      const streakBonus = Math.min(30, attendedDaysCount * 5);
      weekScore = Math.min(100, Math.round(consistencyPct * 70 + streakBonus));
    }

    allWeeks.push({
      weekNum: k,
      label: isCurrent ? "Now" : `W${k}`,
      isCurrent,
      startDate: weekStart,
      endDate: weekEnd,
      dateRangeStr,
      score: weekScore,
      attendedCount: weekAtts.length || attendedDaysCount,
    });
  }

  return { allWeeks, totalWeeks };
};

const getDynamicFeed = (attendances, sessions) => {
  const feed = [];
  
  attendances.forEach(a => {
    const date = new Date(a.attendanceDate);
    const timeLabel = date.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' });
    feed.push({
      id: `att-${a.id}`,
      timestamp: date,
      type: "checkins",
      icon: FiCheckCircle,
      title: "Daily Check-in Logged",
      sub: `${timeLabel} · Loc: ${a.location || "Not recorded"}`,
      pts: "",
      accent: "#6B8A5A",
      bg: "rgba(245,250,242,0.7)"
    });
  });

  sessions.forEach(s => {
    if (s.isCheckedIn) {
      const date = new Date(s.scheduledAt);
      const timeLabel = date.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' });
      feed.push({
        id: `session-${s.id}`,
        timestamp: date,
        type: "sessions",
        icon: FiCalendar,
        title: `Class Booked: ${s.title}`,
        sub: `${timeLabel} · Instructor: ${s.instructorName || "Maya"}`,
        pts: "",
        accent: "#E97818",
        bg: "rgba(255,244,232,0.5)"
      });
    }
  });

  return feed.sort((a, b) => b.timestamp - a.timestamp);
};

const getDynamicTimeline = (profile, attendances, stats) => {
  const timeline = [];
  
  if (stats?.streakDays > 0) {
    timeline.push({
      icon: FiZap,
      color: "orange",
      isActive: true,
      title: `Practice Streak: ${stats.streakDays} Days`,
      time: "Today",
      desc: `Keep up your daily flow. You are currently at a consecutive ${stats.streakDays}-day checked-in streak!`,
      tags: ["Active Streak", `Score: ${stats.wellnessScore}/100`],
      type: "scores"
    });
  }

  if (attendances && attendances.length > 0) {
    timeline.push({
      icon: FiAward,
      color: "olive",
      title: `Consistency Milestone: ${attendances.length} Sessions`,
      time: "Latest",
      desc: `Completed a total of ${attendances.length} practice sessions on the mat. Wellness score expanded to ${stats?.wellnessScore ?? 0}/100.`,
      tags: ["Milestone", `${attendances.length} Classes Done`],
      type: "sessions"
    });

    if (attendances.length >= 10) {
      timeline.push({
        icon: FiAward,
        color: "orange",
        title: "10 Sessions Mastered",
        time: "Achievement",
        desc: "Reached double-digit yoga practices. Silver Yogi status achieved!",
        tags: ["Silver Badge", "10 Sessions"],
        type: "badges"
      });
    }

    if (attendances.length >= 5) {
      timeline.push({
        icon: FiAward,
        color: "olive",
        title: "5 Sessions Completed",
        time: "Milestone",
        desc: "Built a solid habit foundation of 5 practice check-ins on the mat.",
        tags: ["Bronze Badge", "5 Sessions"],
        type: "badges"
      });
    }

    // Map recent check-in logs into timeline cards
    const recentCheckIns = attendances.slice(0, 10);
    recentCheckIns.forEach((att, idx) => {
      const d = new Date(att.attendanceDate);
      const dateLabel = d.toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' });
      timeline.push({
        icon: FiCheckCircle,
        color: idx % 2 === 0 ? "olive" : "orange",
        title: `Check-in Logged #${attendances.length - idx}`,
        time: dateLabel,
        desc: `Logged practice session at ${att.checkInTime || "Scheduled Time"}. Location: ${att.location || "Yoga Studio"}.`,
        tags: ["Session Logged", `Status: ${att.status || "Present"}`],
        type: "sessions"
      });
    });

    const firstAtt = attendances[attendances.length - 1];
    const firstDateLabel = new Date(firstAtt.attendanceDate).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' });
    timeline.push({
      icon: FiPlay,
      color: "olive",
      title: "First Steps on the Mat",
      time: firstDateLabel,
      desc: "You logged your very first check-in and took the first step in your yoga journey. Consistency grows from here.",
      tags: ["First Practice", "Journey Initiated"],
      type: "sessions"
    });
  }

  if (profile?.createdAt) {
    const dateLabel = new Date(profile.createdAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' });
    timeline.push({
      icon: FiFlag,
      color: "orange",
      title: "Wellness Journey Activated",
      time: dateLabel,
      desc: "Registered your profile and joined Divyyog platform. Unlocked access to video library, daily check-ins, and analytics tracking.",
      tags: ["Account Active", `${profile.tier || "Free"} Member`],
      type: "badges"
    });
  }

  return timeline;
};



// ─── MAIN JOURNEY PAGE ────────────────────────────────────────────────────────
export default function MyJourneyPage() {
  const { checkedIn, stats } = useCheckIn();
  const [profile, setProfile] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [achievements, setAchievements] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const [trendPeriod, setTrendPeriod] = useState("8w");
  const [timelineFilter, setTimelineFilter] = useState("all");
  const [feedFilter, setFeedFilter] = useState("all");
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [showFeedModal, setShowFeedModal] = useState(false);
  
  const weekDays = ["Mo","Tu","We","Th","Fr","Sa","Su"];

  useEffect(() => {
    const fetchJourneyData = async () => {
      try {
        const [profileRes, attRes, sessRes, achRes] = await Promise.all([
          userAPI.getProfile().catch(() => ({ data: null })),
          userAPI.getAttendanceHistory().catch(() => ({ data: null })),
          liveSessionAPI.getSessions().catch(() => ({ data: null })),
          achievementAPI.getSummary().catch(() => ({ data: null }))
        ]);
        if (profileRes?.data?.success) setProfile(profileRes.data.data);
        if (attRes?.data?.success) setAttendanceHistory(attRes.data.data || []);
        if (sessRes?.data?.success) setSessions(sessRes.data.data || []);
        if (achRes?.data?.success && achRes.data.data) setAchievements(achRes.data.data);
      } catch (e) {
        console.error("Failed to load journey statistics:", e);
      } finally {
        setLoadingData(false);
      }
    };
    fetchJourneyData();
  }, [checkedIn]);

  // ─── Export & Share Button Handlers
  const handleExportJourney = () => {
    const dataStr = `DIVYYOG JOURNEY REPORT\n=========================\nGenerated: ${new Date().toLocaleDateString()}\n\nUser: ${profile?.name || 'Yogi'}\nStreak: ${stats.streakDays} days\nTotal Sessions: ${attendanceHistory.length}\nWellness Score: ${stats.wellnessScore}/100\n\nPracticed Dates:\n` + 
      attendanceHistory.map(a => ` - ${new Date(a.attendanceDate).toLocaleDateString()} at ${a.checkInTime || 'N/A'} (Location: ${a.location || 'N/A'})`).join("\n");
    const blob = new Blob([dataStr], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Yoga_Journey_${profile?.name || 'User'}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Journey report exported successfully!");
  };

  const handleShareProgress = () => {
    const text = `I'm on a ${stats.streakDays}-day streak with a Wellness Score of ${stats.wellnessScore}/100 on Divyyog! 🙏✨ Join me in my practice.`;
    if (navigator.share) {
      navigator.share({
        title: "My Divyyog Journey",
        text: text,
        url: window.location.origin
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Progress copied to clipboard! Share it with your friends. ✦");
    }
  };

  // ─── Dynamic computations based on real API states
  const completedCount = useMemo(() => {
    return sessions.filter(s => s.isCheckedIn && s.isEnded).length;
  }, [sessions]);

  const weekSessionsData = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayDiff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayDiff);
    monday.setHours(0, 0, 0, 0);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      weekDates.push(getLocalDateString(d));
    }

    const completedSessions = sessions.filter(s => s.isCheckedIn && s.isEnded);
    const counts = weekDates.map(dateStr => {
      return completedSessions.filter(s => getLocalDateString(s.scheduledAt) === dateStr).length;
    });

    const maxCount = Math.max(...counts, 1);
    const bars = counts.map(c => c > 0 ? (c / maxCount) * 100 : 0);

    return { counts, bars };
  }, [sessions]);

  const weekBars = weekSessionsData.bars;
  const thisWeekCount = useMemo(() => weekSessionsData.counts.reduce((sum, c) => sum + c, 0), [weekSessionsData]);

  const lastWeekSessionsCount = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayDiff = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek) - 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayDiff);
    monday.setHours(0, 0, 0, 0);

    const prevWeekDates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      prevWeekDates.push(getLocalDateString(d));
    }

    const completedSessions = sessions.filter(s => s.isCheckedIn && s.isEnded);
    return completedSessions.filter(s => prevWeekDates.includes(getLocalDateString(s.scheduledAt))).length;
  }, [sessions]);

  const weekTrendPct = useMemo(() => {
    if (lastWeekSessionsCount > 0) {
      return Math.round(((thisWeekCount - lastWeekSessionsCount) / lastWeekSessionsCount) * 100);
    }
    return thisWeekCount * 20;
  }, [thisWeekCount, lastWeekSessionsCount]);

  const [trendViewMode, setTrendViewMode] = useState("recent"); // "recent" | "all"
  const [trendWindowStart, setTrendWindowStart] = useState(0);

  const { allWeeks, totalWeeks } = useMemo(() => {
    return getTrendData(profile, attendanceHistory, stats);
  }, [profile, attendanceHistory, stats]);

  const VISIBLE_COUNT = 6;

  useEffect(() => {
    if (totalWeeks > VISIBLE_COUNT) {
      setTrendWindowStart(totalWeeks - VISIBLE_COUNT);
    } else {
      setTrendWindowStart(0);
    }
  }, [totalWeeks]);

  const displayedTrendItems = useMemo(() => {
    if (!allWeeks || allWeeks.length === 0) return [];
    if (trendViewMode === "all" || totalWeeks <= VISIBLE_COUNT) {
      return allWeeks;
    }
    const start = Math.max(0, Math.min(trendWindowStart, totalWeeks - VISIBLE_COUNT));
    return allWeeks.slice(start, start + VISIBLE_COUNT);
  }, [allWeeks, totalWeeks, trendViewMode, trendWindowStart]);

  const trendChartData = useMemo(() => ({
    items: displayedTrendItems,
    totalWeeks,
    allWeeks
  }), [displayedTrendItems, totalWeeks, allWeeks]);

  const canGoPrev = trendWindowStart > 0;
  const canGoNext = trendWindowStart + VISIBLE_COUNT < totalWeeks;

  const handlePrevWindow = () => {
    setTrendViewMode("recent");
    setTrendWindowStart(prev => Math.max(0, prev - (VISIBLE_COUNT - 1)));
  };

  const handleNextWindow = () => {
    setTrendViewMode("recent");
    setTrendWindowStart(prev => Math.min(totalWeeks - VISIBLE_COUNT, prev + (VISIBLE_COUNT - 1)));
  };

  const currentWindowLabel = useMemo(() => {
    if (totalWeeks <= 1) return "Week 1 (Active in Progress)";
    if (totalWeeks <= VISIBLE_COUNT || trendViewMode === "all") return `All Weeks (W1–W${totalWeeks}) Score Flow`;
    const startNum = trendWindowStart + 1;
    const endNum = Math.min(totalWeeks, trendWindowStart + VISIBLE_COUNT);
    return `Showing W${startNum}–W${endNum} of W${totalWeeks} Active Weeks`;
  }, [totalWeeks, trendViewMode, trendWindowStart]);

  const timelineItems = useMemo(() => {
    const list = getDynamicTimeline(profile, attendanceHistory, stats);
    if (timelineFilter === "all") return list;
    return list.filter(item => item.type === timelineFilter);
  }, [profile, attendanceHistory, stats, timelineFilter]);

  const currentMonthName = useMemo(() => {
    return new Date().toLocaleString("en-US", { month: "long", year: "numeric" });
  }, []);

  const calendarDays = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const numDays = lastDayOfMonth.getDate();
    
    const startDayOfWeek = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;
    
    const userAttDates = attendanceHistory.map(a => getLocalDateString(a.attendanceDate));
    
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ isPadding: true });
    }
    
    for (let dNum = 1; dNum <= numDays; dNum++) {
      const d = new Date(year, month, dNum);
      const dateStr = getLocalDateString(d);
      days.push({
        dayNum: dNum,
        dateStr: d.toLocaleDateString("en-IN", { day: 'numeric', month: 'short', year: 'numeric' }),
        dayOfWeek: d.toLocaleDateString("en-US", { weekday: 'short' }),
        attended: userAttDates.includes(dateStr),
        isFuture: d > today
      });
    }
    return days;
  }, [attendanceHistory]);

  const monthAttendanceCount = useMemo(() => calendarDays.filter(d => !d.isPadding && !d.isFuture && d.attended).length, [calendarDays]);
  const pastDaysInMonth = useMemo(() => calendarDays.filter(d => !d.isPadding && !d.isFuture).length, [calendarDays]);
  const monthAttendancePct = useMemo(() => Math.round((monthAttendanceCount / (pastDaysInMonth || 1)) * 100), [monthAttendanceCount, pastDaysInMonth]);

  const filteredFeedAll = useMemo(() => {
    const list = getDynamicFeed(attendanceHistory, sessions);
    if (feedFilter === "all") return list;
    return list.filter(item => item.type === feedFilter);
  }, [attendanceHistory, sessions, feedFilter]);

  const feedItems = useMemo(() => filteredFeedAll.slice(0, 6), [filteredFeedAll]);

  const personalBestStreak = useMemo(() => getPersonalBestStreak(attendanceHistory), [attendanceHistory]);

  const isAchievementsPermitted = isFeaturePermitted(profile, "achievements");
  const isAttendancePermitted = isFeaturePermitted(profile, "attendance_logs");

  const badgesEarned = useMemo(() => {
    if (!isAchievementsPermitted) return 0;
    if (achievements?.unlockedCount !== undefined) {
      return achievements.unlockedCount;
    }
    if (achievements?.badges && Array.isArray(achievements.badges)) {
      return achievements.badges.filter(b => b.unlocked).length;
    }
    return 0;
  }, [achievements, isAchievementsPermitted]);

  const totalBadges = useMemo(() => {
    return achievements?.badges?.length || 0;
  }, [achievements]);

  const statsForBand = useMemo(() => ({
    streakDays: stats.streakDays,
    bestStreak: personalBestStreak,
    totalPractices: attendanceHistory.length || completedCount,
    hoursPracticed: Math.round((attendanceHistory.length || completedCount) * 0.75),
    badgesEarned: badgesEarned,
    totalBadges: totalBadges,
    wellnessScore: stats.wellnessScore,
    isAchievementsLocked: !isAchievementsPermitted,
    isAttendanceLocked: !isAttendancePermitted
  }), [stats.streakDays, stats.wellnessScore, personalBestStreak, attendanceHistory.length, completedCount, badgesEarned, totalBadges, isAchievementsPermitted, isAttendancePermitted]);

  if (loadingData) {
    return (
      <UserDashboardLayout>
        <BrandLoader 
          message="Loading Journey Dashboard..." 
          subMessage="Calculating your milestones, streaks, and practice path" 
        />
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="relative z-10 w-full min-w-0" style={{ fontFamily: "'Inter',sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

        {/* Ambient orbs */}
        <motion.div animate={{ y: [0,-25,0], x:[0,14,0] }} transition={{ duration:12, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.09),transparent 70%)", filter:"blur(80px)", top:-150, right:-100 }} />
        <motion.div animate={{ y:[0,20,0], x:[0,-10,0] }} transition={{ duration:15, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", bottom:-100, left:-80 }} />

        <Particles />
        <Topbar />
        <HeroHeader profile={profile} stats={stats} onExport={handleExportJourney} onShare={handleShareProgress} />
        <StatsBand stats={statsForBand} />

        {/* Trend + Weekly Bars */}
        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
          className="grid grid-cols-1 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_340px] gap-5 mb-5">
            <TiltCard intensity={5} className="rounded-[28px] sm:rounded-[32px] p-5 sm:p-8" style={glass}>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-0.4px" }}>Wellness Score Trend</h2>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-700 border border-amber-500/20">
                      {totalWeeks === 1 ? "Week 1 · Now" : `Week ${totalWeeks}`}
                    </span>
                  </div>
                  <p className="text-[12px] text-[#AAA] mt-0.5">{currentWindowLabel}</p>
                </div>
                
                {/* Pagination & Filter Controls */}
                <div className="flex items-center gap-2 flex-wrap">
                  {totalWeeks > VISIBLE_COUNT && (
                    <div className="flex items-center gap-1 bg-white/70 rounded-full p-1 border border-zinc-200/80 shadow-sm">
                      <button
                        onClick={handlePrevWindow}
                        disabled={!canGoPrev}
                        title="View earlier past weeks"
                        className={`w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-all ${canGoPrev ? 'bg-[#F29A0C]/15 text-[#E97818] hover:bg-[#F29A0C] hover:text-white' : 'opacity-30 cursor-not-allowed text-zinc-400'}`}>
                        <FiChevronLeft size={14} />
                      </button>
                      <span className="text-[10px] font-bold text-zinc-500 px-1.5 whitespace-nowrap">
                        W{trendWindowStart + 1}–W{Math.min(totalWeeks, trendWindowStart + VISIBLE_COUNT)}
                      </span>
                      <button
                        onClick={handleNextWindow}
                        disabled={!canGoNext}
                        title="View latest weeks"
                        className={`w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-all ${canGoNext ? 'bg-[#F29A0C]/15 text-[#E97818] hover:bg-[#F29A0C] hover:text-white' : 'opacity-30 cursor-not-allowed text-zinc-400'}`}>
                        <FiChevronRight size={14} />
                      </button>
                    </div>
                  )}

                  <div className="ud-scroll flex gap-1.5">
                    <FilterPill
                      label={totalWeeks > VISIBLE_COUNT ? "Recent 6W" : `Current (${totalWeeks}W)`} 
                      active={trendViewMode === "recent"} 
                      onClick={() => {
                        setTrendViewMode("recent");
                        if (totalWeeks > VISIBLE_COUNT) setTrendWindowStart(totalWeeks - VISIBLE_COUNT);
                      }} 
                    />
                    {totalWeeks > VISIBLE_COUNT && (
                      <>
                        <FilterPill 
                          label="Past (W1–W6)" 
                          active={trendViewMode === "recent" && trendWindowStart === 0} 
                          onClick={() => {
                            setTrendViewMode("recent");
                            setTrendWindowStart(0);
                          }} 
                        />
                        <FilterPill 
                          label="All Weeks" 
                          active={trendViewMode === "all"} 
                          onClick={() => setTrendViewMode("all")} 
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
              <TrendChart data={trendChartData} />
            </TiltCard>

            <TiltCard intensity={6} className="rounded-[32px] p-7" style={glass}>
              <h2 className="font-black text-[#1F1F1F] mb-1" style={{ fontFamily:"'Poppins',sans-serif", fontSize:18, letterSpacing:"-0.3px" }}>This Week</h2>
              <p className="text-[12px] text-[#AAA] mb-5">Sessions per day</p>
              <div className="flex items-end justify-between gap-2" style={{ height: 110 }}>
                {weekBars.map((h, i) => {
                  const sCount = weekSessionsData.counts[i];
                  return (
                    <div key={i} title={`${sCount} session${sCount === 1 ? '' : 's'}`} className="flex-1 h-full flex flex-col justify-end items-center gap-1.5">
                      <motion.div
                        className="w-full rounded-t-lg cursor-pointer animate-height"
                        whileHover={h > 0 ? { scaleY: 1.06, filter: "brightness(1.1)" } : {}}
                        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: i * 0.08, duration: 0.5, ease: "easeOut" }}
                        style={{
                          transformOrigin: "bottom",
                          height: `${Math.max(h, 5)}%`,
                          background: h > 0 ? "linear-gradient(180deg,#F29A0C,#E97818)" : "rgba(239,229,216,0.4)",
                          boxShadow: h>0 ? "0 4px 14px rgba(242,154,12,0.25)" : "none"
                        }}
                      />
                      <span className="text-[10px] font-bold" style={{ color: h>0 ? "#F29A0C" : "#DDD" }}>{weekDays[i]}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 rounded-[16px] p-4 flex justify-between items-center" style={{ background:"rgba(255,244,232,0.7)" }}>
                <div>
                  <p className="text-[10px] font-bold text-[#AAA] uppercase tracking-[0.6px]">Week Total</p>
                  <p className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:22 }}>{thisWeekCount} <span className="text-[13px] font-medium text-[#AAA]">sessions</span></p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-[#AAA] uppercase tracking-[0.6px]">vs last week</p>
                  <p className="font-black text-[#6B8A5A]" style={{ fontSize:16 }}>
                    {weekTrendPct >= 0 ? `+${weekTrendPct}%` : `${weekTrendPct}%`} 
                    <FiTrendingUp style={{ display:"inline", fontSize:13, marginLeft: 4 }} />
                  </p>
                </div>
              </div>
            </TiltCard>
          </motion.div>

          {/* Timeline + Heatmap */}
          <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
            className="grid grid-cols-1 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px] gap-5 mb-5">
            {/* Timeline */}
            <div className="rounded-[28px] sm:rounded-[32px] p-5 sm:p-8" style={glass}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 sm:gap-4 mb-6">
                <div>
                  <h2 className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-0.4px" }}>Progress Timeline</h2>
                  <p className="text-[12px] text-[#AAA] mt-0.5">Your journey milestones</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {["All","Sessions","Badges","Scores"].map(f => <FilterPill key={f} label={f} active={timelineFilter===f.toLowerCase()} onClick={() => setTimelineFilter(f.toLowerCase())} />)}
                </div>
              </div>
              <div className="relative pl-12">
                {/* Timeline vertical line */}
                <div className="absolute left-[22px] top-0 bottom-0 w-[2px] rounded-full"
                  style={{ background: "linear-gradient(180deg,#F29A0C,#E97818,#6B8A5A,rgba(107,138,90,0.2))" }} />
                {timelineItems.length === 0 ? (
                  <p className="text-xs text-[#AAA] italic py-8 pl-4">No milestones logged yet.</p>
                ) : (
                  timelineItems.slice(0, 4).map((item, i) => (
                    <TimelineCard key={item.title + i} item={item} delay={i * 0.08} />
                  ))
                )}
              </div>
              
              {timelineItems.length > 4 && (
                <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }}
                  onClick={() => setShowTimelineModal(true)}
                  className="mt-4 w-full h-11 rounded-full text-[13px] font-bold text-[#C8660A] flex items-center justify-center gap-2 cursor-pointer transition-transform active:scale-95"
                  style={{ background:"rgba(255,244,232,0.9)", border:"1px solid rgba(242,154,12,0.3)", fontFamily:"'Inter',sans-serif", boxShadow:"0 4px 14px rgba(242,154,12,0.12)" }}>
                  <FiAward size={14} /> Load More History ({timelineItems.length - 4} More Milestones)
                </motion.button>
              )}
            </div>

            {/* Heatmap + Streak banner */}
            <div className="flex flex-col gap-5">
              <TiltCard intensity={5} className="rounded-[32px] p-6" style={glass}>
                <div className="flex justify-between items-start mb-5">
                  <div>
                    <h3 className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:17, letterSpacing:"-0.3px" }}>Attendance Calendar</h3>
                    <p className="text-[12px] text-[#AAA] mt-0.5">{currentMonthName} · {monthAttendanceCount} / {pastDaysInMonth} attended</p>
                  </div>
                  <p className="font-black text-[#6B8A5A]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:24 }}>
                    {monthAttendancePct}%
                  </p>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["M","T","W","T","F","S","S"].map((d,i) => <p key={i} className="text-center text-[9px] font-bold text-[#CCC]">{d}</p>)}
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {calendarDays.map((d, i) => {
                    if (d.isPadding) {
                      return <div key={`pad-${i}`} className="aspect-square bg-transparent" />;
                    }
                    
                    let bg = "rgba(239,229,216,0.3)";
                    let border = "1px solid transparent";
                    let color = "#AAA";
                    
                    if (d.attended) {
                      bg = "linear-gradient(135deg,#6B8A5A,#8BA47A)";
                      color = "#fff";
                    } else if (d.isFuture) {
                      bg = "transparent";
                      border = "1px dashed rgba(239,229,216,0.8)";
                      color = "#EEE";
                    } else {
                      bg = "rgba(239,229,216,0.6)";
                      color = "#888";
                    }
                    
                    return (
                      <motion.div
                        key={`day-${d.dayNum}`}
                        title={d.isFuture ? `${d.dateStr}: Future` : `${d.dateStr} (${d.dayOfWeek}): ${d.attended ? 'Present' : 'Missed'}`}
                        whileHover={!d.isFuture ? { scale: 1.12, zIndex: 5 } : {}}
                        className="aspect-square rounded-[8px] flex items-center justify-center text-[10px] font-bold cursor-pointer relative"
                        style={{ background: bg, border: border, color: color }}
                      >
                        {d.dayNum}
                        {d.attended && (
                          <span className="absolute bottom-1 w-1 h-1 rounded-full bg-white opacity-80" />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-[3px]" style={{ background:"linear-gradient(135deg,#6B8A5A,#8BA47A)" }} /><span className="text-[10px] text-[#AAA]">Present</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-[3px]" style={{ background:"rgba(239,229,216,0.6)" }} /><span className="text-[10px] text-[#AAA]">Missed</span></div>
                </div>
              </TiltCard>

              {/* Best streak banner */}
              <motion.div whileHover={{ y:-4 }}
                className="relative overflow-hidden rounded-[28px] p-6"
                style={{ background:"linear-gradient(145deg,#F29A0C,#E97818,#C5600A)", boxShadow:"0 16px 40px rgba(242,154,12,0.4)" }}>
                <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
                  style={{ backgroundImage:"radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize:"16px 16px" }} />
                <div className="absolute top-[-30px] right-[-30px] w-28 h-28 rounded-full opacity-10"
                  style={{ background:"radial-gradient(circle,#fff,transparent)", filter:"blur(20px)" }} />
                <div className="relative">
                  <p className="text-white/65 text-[10px] font-bold tracking-[1.5px] uppercase mb-2">Personal Best Streak</p>
                  <p className="font-black text-white leading-none mb-2" style={{ fontFamily:"'Poppins',sans-serif", fontSize:48, letterSpacing:"-2px" }}>
                    {personalBestStreak}<span className="text-lg font-semibold tracking-normal ml-1">days</span>
                  </p>
                  <p className="text-white/75 text-[12px] mb-4">
                    Streak is calculated from consecutive daily check-ins. Check in today or yesterday to keep it building.
                  </p>
                  <div className="h-1.5 rounded-full mb-1.5" style={{ background:"rgba(255,255,255,0.2)" }}>
                    <div className="h-full rounded-full" style={{ width:`${personalBestStreak > 0 ? Math.min(100, (stats.streakDays / personalBestStreak) * 100) : 0}%`, background:"rgba(255,255,255,0.9)" }} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] text-white/65">Current: {stats.streakDays}</span>
                    <span className="text-[10px] text-white/65">Best: {personalBestStreak}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Activity Feed */}
          <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
            className="rounded-[28px] sm:rounded-[32px] p-5 sm:p-8 mb-5" style={glass}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 sm:gap-4 mb-6">
              <div>
                <h2 className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-0.4px" }}>Activity Feed</h2>
                <p className="text-[12px] text-[#AAA] mt-0.5">Everything you've done on the platform</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["All","Sessions","Videos","Badges"].map(f => <FilterPill key={f} label={f} active={feedFilter===f.toLowerCase()} onClick={() => setFeedFilter(f.toLowerCase())} />)}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {feedItems.length === 0 ? (
                <p className="col-span-1 sm:col-span-2 text-xs text-[#AAA] italic py-8 text-center bg-white/40 rounded-[18px]">No recent activity logged.</p>
              ) : (
                feedItems.map((item, i) => (
                  <motion.div key={item.id}
                    initial={{ opacity:0, y:16 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
                    transition={{ delay:i*0.07 }}
                    whileHover={{ x:6, background: item.bg.replace("0.7","0.9").replace("0.5","0.8").replace("0.4","0.7") }}
                    className="flex items-center gap-3 px-4 py-3 rounded-[18px] cursor-default"
                    style={{ background:item.bg, transition:"all 0.22s" }}>
                    <div className="w-10 h-10 rounded-[14px] flex items-center justify-center flex-shrink-0"
                      style={{ background:`linear-gradient(135deg,${item.accent},${item.accent}BB)`, boxShadow:`0 4px 12px ${item.accent}40` }}>
                      <item.icon style={{ color:"#fff", fontSize:16 }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-[#1F1F1F] truncate">{item.title}</p>
                      <p className="text-[11px] text-[#AAA] mt-0.5 truncate">{item.sub}</p>
                    </div>
                    {item.pts && (
                      <span className="px-3 py-1.5 rounded-full text-[10px] font-bold text-white flex-shrink-0"
                        style={{ background:item.accent }}>{item.pts}</span>
                    )}
                  </motion.div>
                ))
              )}
            </div>
            {filteredFeedAll.length > 6 && (
              <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }}
                onClick={() => setShowFeedModal(true)}
                className="mt-5 w-full h-11 rounded-full text-[13px] font-semibold text-[#666] flex items-center justify-center gap-2 cursor-pointer"
                style={{ background:"rgba(255,255,255,0.9)", border:"1px solid rgba(239,229,216,0.9)", fontFamily:"'Inter',sans-serif" }}>
                <FiAward size={13} /> Load Full Activity Log
              </motion.button>
            )}
          </motion.div>
        </div>

      {/* Timeline Modal */}
      <AnimatePresence>
        {showTimelineModal && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowTimelineModal(false)}
              className="absolute inset-0 bg-[#1F1F1F]/40 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              transition={{ type: "spring", damping: 25, stiffness: 220 }}
              className="relative w-full max-w-[780px] rounded-[36px] overflow-hidden p-8 "
              style={glass}
            >
              <button
                onClick={() => setShowTimelineModal(false)}
                className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 bg-white/80 cursor-pointer shadow-sm text-gray-500 hover:text-gray-800"
              >
                <FiX size={15} />
              </button>
              
              <h2 className="text-[20px] font-black text-gray-900 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Full Progress Timeline ✦
              </h2>
              
              <div className="max-h-[480px] overflow-y-auto pr-2 relative pl-8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                <div className="absolute left-[14px] top-0 bottom-0 w-[2px] rounded-full"
                  style={{ background: "linear-gradient(180deg,#F29A0C,#E97818,#6B8A5A,rgba(107,138,90,0.2))" }} />
                {timelineItems.map((item, i) => (
                  <TimelineCard key={item.title + i} item={item} delay={0} />
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Activity Feed Modal */}
      <AnimatePresence>
        {showFeedModal && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFeedModal(false)}
              className="absolute inset-0 bg-[#1F1F1F]/40 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              transition={{ type: "spring", damping: 25, stiffness: 220 }}
              className="relative w-full max-w-[820px] rounded-[36px] overflow-hidden p-8"
              style={glass}
            >
              <button
                onClick={() => setShowFeedModal(false)}
                className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 bg-white/80 cursor-pointer shadow-sm text-gray-500 hover:text-gray-800"
              >
                <FiX size={15} />
              </button>
              
              <h2 className="text-[20px] font-black text-gray-900 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Full Activity Feed ✦
              </h2>
              
              <div className="max-h-[480px] overflow-y-auto pr-2 grid grid-cols-1 md:grid-cols-2 gap-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {filteredFeedAll.map((item, i) => (
                  <motion.div key={item.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-[18px] cursor-default"
                    style={{ background:item.bg }}>
                    <div className="w-10 h-10 rounded-[14px] flex items-center justify-center flex-shrink-0"
                      style={{ background:`linear-gradient(135deg,${item.accent},${item.accent}BB)` }}>
                      <item.icon style={{ color:"#fff", fontSize:16 }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-[#1F1F1F] truncate">{item.title}</p>
                      <p className="text-[11px] text-[#AAA] mt-0.5 truncate">{item.sub}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}